/**
 * Scored question banks per typology — for InvestigationScoredStrategy
 * Each bank: { label, colour, maxScore, thresholds: { suspicious, inconclusive }, intro, sections: [{ title, icon, questions }] }
 */

import { DollarSign, Eye, User, Clock, Shield, Users, CreditCard } from 'lucide-react'

const C = {
  pri: '#0a5169', red: '#dc2626', amber: '#d97706', green: '#16a34a',
  purple: '#7c3aed', slate: '#64748b',
}

const commonOptions = (positive, neutral, negative) => [
  { label: 'Yes', score: positive },
  { label: 'No', score: negative },
  { label: 'Unclear / Not checked', score: neutral },
]

export const BANK_STRUCTURING = {
  label: 'Structuring / Near-Threshold',
  colour: C.red,
  maxScore: 42,
  thresholds: { suspicious: 26, inconclusive: 14 },
  intro: 'The alert fired on a structuring or near-threshold pattern. These questions help determine whether this is deliberate structuring or legitimate gaming.',
  sections: [
    {
      title: 'Transaction Pattern',
      icon: DollarSign,
      questions: [
        { id: 's1', weight: 4, text: 'Do the individual payout amounts cluster just below $10,000?', hint: 'Structuring keeps amounts under threshold.', options: [
          { label: 'Yes — amounts consistently just under $10k', score: 4 },
          { label: 'No — amounts vary without pattern near $10k', score: -2 },
          { label: 'Mixed — some near threshold, some not', score: 2 },
          { label: 'Not checked yet', score: 1 },
        ]},
        { id: 's2', weight: 3, text: 'Is there a recurring velocity pattern across multiple visits?', hint: 'One incident could be luck; a pattern indicates intent.', options: [
          { label: 'Yes — pattern appears in prior visits', score: 3 },
          { label: 'No — first velocity event on record', score: -2 },
          { label: 'Unclear', score: 1 },
        ]},
      ],
    },
    {
      title: 'Patron Behaviour',
      icon: Eye,
      questions: [
        { id: 's3', weight: 4, text: 'Did the patron refuse electronic payment (EFT, cheque)?', hint: 'Cash insistence is a strong structuring indicator.', options: [
          { label: 'Yes — refusal documented', score: 4 },
          { label: 'No — patron accepted alternatives', score: -2 },
          { label: 'EFT was not offered', score: 0 },
        ]},
        { id: 's4', weight: 3, text: 'Was the patron observed arriving with cash (no venue ATM use)?', options: [
          { label: 'Yes — staff observed cash / no ATM use', score: 3 },
          { label: 'No — ATM used or no unusual behaviour', score: -1 },
          { label: 'CCTV not yet reviewed', score: 1 },
        ]},
      ],
    },
    {
      title: 'Source of Funds & Profile',
      icon: User,
      questions: [
        { id: 's5', weight: 5, text: "Is the patron's payout volume plausible given their declared occupation and income?", options: [
          { label: 'No — activity significantly exceeds plausible income', score: 5 },
          { label: 'Possibly — activity high but occupation is cash-heavy', score: 2 },
          { label: 'Yes — consistent with known income', score: -3 },
          { label: 'Cannot assess — insufficient information', score: 2 },
        ]},
        { id: 's6', weight: 4, text: 'Has the patron provided source of funds documentation?', options: [
          { label: 'No — not yet requested or provided', score: 3 },
          { label: 'Partially — some documentation, not fully verified', score: 1 },
          { label: 'Yes — documentation provided and verified', score: -4 },
          { label: 'Requested — awaiting response', score: 2 },
        ]},
      ],
    },
  ],
}

export const BANK_VELOCITY = {
  label: 'Velocity / Rapid Payout',
  colour: C.amber,
  maxScore: 36,
  thresholds: { suspicious: 22, inconclusive: 12 },
  intro: 'The alert fired on a velocity pattern. These questions help determine whether this is cash cycling or legitimate multi-EGM play.',
  sections: [
    {
      title: 'Payout Pattern',
      icon: DollarSign,
      questions: [
        { id: 'v1', weight: 4, text: 'Are there 4 or more payouts within a 48-hour window?', options: [
          { label: 'Yes', score: 4 },
          { label: 'No — fewer than 4', score: -2 },
          { label: 'Unclear', score: 1 },
        ]},
        { id: 'v2', weight: 4, text: 'Are payouts spread across multiple EGMs in the same session?', options: [
          { label: 'Yes', score: 4 },
          { label: 'No — single EGM', score: -1 },
          { label: 'Unclear', score: 1 },
        ]},
      ],
    },
    {
      title: 'Play vs Collection',
      icon: Eye,
      questions: [
        { id: 'v3', weight: 5, text: 'Did the patron genuinely play each EGM (session data confirms)?', options: [
          { label: 'No — collected without play', score: 5 },
          { label: 'Yes — play confirmed', score: -3 },
          { label: 'Unclear — session data not reviewed', score: 2 },
        ]},
        { id: 'v4', weight: 3, text: 'Is payout volume unusual vs patron baseline?', options: [
          { label: 'Yes — significant escalation', score: 3 },
          { label: 'No — within normal range', score: -2 },
          { label: 'No prior baseline', score: 1 },
        ]},
      ],
    },
    {
      title: 'Source of Funds',
      icon: User,
      questions: [
        { id: 'v5', weight: 4, text: 'Has SOF been provided and does it explain the volume?', options: [
          { label: 'No — not provided', score: 3 },
          { label: 'Yes — explains volume', score: -3 },
          { label: 'Provided — does not explain', score: 4 },
        ]},
      ],
    },
  ],
}

export const BANK_THIRD_PARTY = {
  label: 'Third-Party Collection',
  colour: C.purple,
  maxScore: 24,
  thresholds: { suspicious: 16, inconclusive: 8 },
  intro: 'Different person observed collecting payout from the one who played. These questions assess authorisation and pattern.',
  sections: [
    {
      title: 'Observation',
      icon: Eye,
      questions: [
        { id: 'tp1', weight: 5, text: 'Was the collector confirmed as a different person from the player? (CCTV / staff)', options: [
          { label: 'Yes — different person confirmed', score: 5 },
          { label: 'No — same person', score: -5 },
          { label: 'Unclear', score: 2 },
        ]},
        { id: 'tp2', weight: 4, text: 'Is there documented authorisation (carer, power of attorney)?', options: [
          { label: 'No — undocumented', score: 4 },
          { label: 'Yes — documented', score: -2 },
          { label: 'Unclear', score: 1 },
        ]},
      ],
    },
    {
      title: 'Pattern',
      icon: Users,
      questions: [
        { id: 'tp3', weight: 3, text: 'Is this a recurring pattern (multiple occasions)?', options: [
          { label: 'Yes — recurring', score: 3 },
          { label: 'No — one-off', score: -1 },
          { label: 'Unclear', score: 1 },
        ]},
      ],
    },
  ],
}

export const BANK_EFT_REFUSAL = {
  label: 'EFT / Cash Preference',
  colour: C.amber,
  maxScore: 28,
  thresholds: { suspicious: 18, inconclusive: 10 },
  intro: 'Patron has refused electronic payment. These questions assess whether the preference is suspicious or has a legitimate basis.',
  sections: [
    {
      title: 'Refusal Pattern',
      icon: CreditCard,
      questions: [
        { id: 'e1', weight: 4, text: 'Has the patron refused EFT on multiple occasions?', options: [
          { label: 'Yes — pattern of refusal', score: 4 },
          { label: 'No — once or rare', score: -2 },
          { label: 'Unclear', score: 1 },
        ]},
        { id: 'e2', weight: 4, text: 'Did refusal occur particularly for larger payout amounts?', options: [
          { label: 'Yes', score: 4 },
          { label: 'No', score: -1 },
          { label: 'Unclear', score: 1 },
        ]},
      ],
    },
    {
      title: 'Reason & Profile',
      icon: User,
      questions: [
        { id: 'e3', weight: 4, text: 'Does the patron have a bank account on their member profile?', options: [
          { label: 'Yes — refusal despite having account', score: 4 },
          { label: 'No / unknown — possibly unbanked', score: -1 },
          { label: 'Unclear', score: 1 },
        ]},
        { id: 'e4', weight: 3, text: 'Was a plausible reason given (e.g. no bank account)?', options: [
          { label: 'No — vague or refused to explain', score: 3 },
          { label: 'Yes — plausible', score: -2 },
          { label: 'Not asked', score: 1 },
        ]},
      ],
    },
  ],
}

export const BANK_SOF_MISMATCH = {
  label: 'Source of Funds Mismatch',
  colour: C.purple,
  maxScore: 32,
  thresholds: { suspicious: 20, inconclusive: 10 },
  intro: 'Gaming volume appears inconsistent with patron profile or declared SOF. These questions assess the mismatch.',
  sections: [
    {
      title: 'Profile Consistency',
      icon: User,
      questions: [
        { id: 'sof1', weight: 5, text: "Is the patron's payout volume plausible given their declared occupation and income?", options: [
          { label: 'No — significantly exceeds plausible income', score: 5 },
          { label: 'Possibly — high but occupation is cash-heavy', score: 2 },
          { label: 'Yes — consistent', score: -3 },
          { label: 'Cannot assess — insufficient information', score: 2 },
        ]},
        { id: 'sof2', weight: 4, text: 'Has the patron provided SOF documentation?', options: [
          { label: 'No — not requested or provided', score: 3 },
          { label: 'Yes — provided and verified', score: -4 },
          { label: 'Requested — awaiting response', score: 2 },
          { label: 'Provided — does not explain volume', score: 4 },
        ]},
      ],
    },
    {
      title: 'Escalation',
      icon: Clock,
      questions: [
        { id: 'sof3', weight: 3, text: "Has this patron's activity level escalated recently?", options: [
          { label: 'Yes — significant escalation', score: 3 },
          { label: 'Moderate — some increase', score: 1 },
          { label: 'No — consistent with history', score: -2 },
        ]},
      ],
    },
  ],
}

export const BANK_PEP_SANCTIONS = {
  label: 'PEP / Sanctions',
  colour: C.red,
  maxScore: 24,
  thresholds: { suspicious: 16, inconclusive: 8 },
  intro: 'AML screening returned a hit. PEP requires ECDD regardless of activity. Sanctions requires immediate MLRO contact.',
  sections: [
    {
      title: 'Screening Result',
      icon: Shield,
      questions: [
        { id: 'pep1', weight: 5, text: 'Is the screening hit CONFIRMED or POSSIBLE?', options: [
          { label: 'Confirmed', score: 5 },
          { label: 'Possible — disambiguation needed', score: 2 },
          { label: 'Ruled out', score: -5 },
        ]},
        { id: 'pep2', weight: 5, text: 'Is the patron on a SANCTIONS list?', options: [
          { label: 'Yes', score: 5 },
          { label: 'No — PEP only', score: 2 },
          { label: 'Unclear', score: 1 },
        ]},
      ],
    },
    {
      title: 'Activity',
      icon: User,
      questions: [
        { id: 'pep3', weight: 3, text: 'Is gaming activity above LOW threshold?', options: [
          { label: 'Yes', score: 3 },
          { label: 'No — minimal activity', score: -1 },
          { label: 'Unclear', score: 1 },
        ]},
        { id: 'pep4', weight: 3, text: 'Does activity raise suspicion beyond PEP status?', options: [
          { label: 'Yes', score: 3 },
          { label: 'No', score: -1 },
          { label: 'Unclear', score: 1 },
        ]},
      ],
    },
  ],
}

export const BANK_FOR_TYPOLOGY = {
  structuring: BANK_STRUCTURING,
  velocity: BANK_VELOCITY,
  third_party: BANK_THIRD_PARTY,
  eft_refusal: BANK_EFT_REFUSAL,
  sof_mismatch: BANK_SOF_MISMATCH,
  pep_sanctions: BANK_PEP_SANCTIONS,
}
